import React, { useEffect, useRef, useState } from 'react';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { useLocation } from 'react-router-dom';
import { checkAuth } from 'src/utils/checkauth';
import { getCurrentUser } from 'src/utils/getCurrentUser';
import { useProfile } from 'src/hooks/useProfile';
import { apiClient } from 'src/api/apiClient';
import { INTERCOM_IDENTITY_VERIFICATION } from 'src/constants/httpEndPoints';

declare global {
  interface Window {
    Intercom?: any;
    intercomSettings?: any;
  }
}

const INTERCOM_SCRIPT_ID = 'intercom-widget-script';

const ensureIntercomStub = () => {
  if (window.Intercom) return;
  const intercomStub: any = (...args: any[]) => {
    intercomStub.q.push(args);
  };
  intercomStub.q = [];
  window.Intercom = intercomStub;
};

const injectIntercomScript = (appId: string) => {
  if (!appId) return;
  if (document.getElementById(INTERCOM_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = INTERCOM_SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.src = `https://widget.intercom.io/widget/${appId}`;
  document.body.appendChild(script);
};

const cleanupIntercomDom = () => {
  // Script tag
  const script = document.getElementById(INTERCOM_SCRIPT_ID);
  if (script?.parentNode) script.parentNode.removeChild(script);

  // Common containers/iframes created by Intercom
  const candidates = [
    '#intercom-container',
    '.intercom-lightweight-app',
    '.intercom-lightweight-app-launcher',
    '.intercom-launcher',
    '.intercom-launcher-frame',
    'iframe[name^="intercom-"]',
  ];

  candidates.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => el.remove());
  });
};

export const IntercomMessenger: React.FC = () => {
  const location = useLocation();
  const { emailId, customerId, clientName } = useProfile();

  // Dedicated feature flag for the support widget (separate from "Intercom Integration")
  const featureFlags = useFeatureFlags();
  const enabled: boolean = Boolean(featureFlags.intercomMessenger);

  // App ID is required for the widget script URL
  const appId = String(process.env.REACT_APP_INTERCOM_APP_ID || '');

  const [bootPayload, setBootPayload] = useState<any>(null);
  const bootedRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Build boot payload from JWT (best source for name + user id)
    const hydrate = async () => {
      if (!enabled) {
        setBootPayload(null);
        return;
      }

      if (!appId) {
        // Misconfigured env; don't attempt to boot.
        setBootPayload(null);
        return;
      }

      if (!checkAuth()) {
        setBootPayload(null);
        return;
      }

      // Prefer Redux email (already used app-wide); fallback to token decode if needed.
      let user: any = null;
      try {
        user = await getCurrentUser();
      } catch {
        user = null;
      }

      const loggedInEmail = (emailId || user?.email || user?.sub || '').toString().trim();
      if (!loggedInEmail) {
        setBootPayload(null);
        return;
      }

      // Important: For Intercom conversations, we want the *user identity* to be the logged-in email.
      // Use email as both user_id and email to avoid Intercom treating the company/tenant as the user.
      const intercomUserId = loggedInEmail;

      const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
      const name = fullName || undefined;

      // If Intercom "Messenger Security" is enabled, Intercom requires either:
      // - user_hash (HMAC SHA256 on your backend using Intercom identity verification secret), OR
      // - intercom_user_jwt (signed on your backend).
      //
      // We cannot compute these client-side; if an endpoint is provided, fetch and attach it.
      let identity: { user_hash?: string; intercom_user_jwt?: string } = {};
      if (INTERCOM_IDENTITY_VERIFICATION) {
        try {
          const res = await apiClient.post(
            INTERCOM_IDENTITY_VERIFICATION,
            {
              user_id: intercomUserId,
            },
            {
              headers: {
                'X-Login-Email': emailId,
                'X-Customer-Id': customerId,
              },
            },
          );
          const payload = res?.data?.data || res?.data?.obj || res?.data || {};
          // Backend may return either:
          // - { user_hash: "..." } / { intercom_user_jwt: "..." }
          // - or the raw hash string directly (e.g. "94bb...")
          if (typeof payload === 'string') {
            identity = { user_hash: payload };
          } else {
            identity = {
              user_hash: payload.user_hash || payload.userHash,
              intercom_user_jwt: payload.intercom_user_jwt || payload.intercomUserJwt,
            };
          }
        } catch {
          // If identity verification is required and backend isn't ready, Intercom will reject boot.
          identity = {};
        }
      }

      setBootPayload({
        app_id: appId,
        user_id: intercomUserId,
        email: loggedInEmail,
        ...(name ? { name } : {}),
        ...(customerId ? { company: { id: customerId, name: clientName || customerId } } : {}),
        // Optional custom attributes for debugging/triage in Intercom
        ...(customerId ? { customer_id: customerId } : {}),
        ...(clientName ? { tenant_name: clientName } : {}),
        ...(identity.user_hash ? { user_hash: identity.user_hash } : {}),
        ...(identity.intercom_user_jwt ? { intercom_user_jwt: identity.intercom_user_jwt } : {}),
      });
    };

    hydrate().catch(() => {
      setBootPayload(null);
    });
  // Include pathname so login -> app navigation triggers a re-hydrate without manual refresh
  }, [enabled, appId, emailId, customerId, clientName, location.pathname]);

  useEffect(() => {
    // Boot / Update / Shutdown
    if (!enabled) {
      if (window.Intercom) {
        try {
          window.Intercom('shutdown');
        } catch {
          // ignore
        }
      }
      bootedRef.current = false;
      currentUserIdRef.current = null;
      window.intercomSettings = undefined;
      try {
        delete window.Intercom;
      } catch {
        // ignore
      }
      cleanupIntercomDom();
      return;
    }

    if (!appId || !bootPayload) return;

    ensureIntercomStub();
    window.intercomSettings = bootPayload;
    injectIntercomScript(appId);

    try {
      const nextUserId = String(bootPayload?.user_id || '');
      // Intercom requires a full shutdown + boot to switch users. Calling `update` is not enough.
      if (bootedRef.current && currentUserIdRef.current && nextUserId && currentUserIdRef.current !== nextUserId) {
        window.Intercom('shutdown');
        bootedRef.current = false;
        currentUserIdRef.current = null;
      }

      if (!bootedRef.current) {
        window.Intercom('boot', bootPayload);
        bootedRef.current = true;
        currentUserIdRef.current = nextUserId || null;
      } else {
        window.Intercom('update', bootPayload);
      }
    } catch {
      // ignore
    }
  }, [enabled, appId, bootPayload]);

  useEffect(() => {
    // Keep Intercom context updated on navigation
    if (!enabled || !window.Intercom || !bootedRef.current) return;
    try {
      window.Intercom('update');
    } catch {
      // ignore
    }
  }, [enabled, location.pathname]);

  return null;
};

export default IntercomMessenger;


